import React, {useState} from 'react';
import {useAuth} from '../../../helpers/AuthContext';
import useSellerTax from './useSellerTax';
import TaxSummaryCards from './TaxSummaryCards';
import TaxJurisdictionTable from './TaxJurisdictionTable';
import TaxOrderTable from './TaxOrderTable';
import {downloadTaxCsv} from './taxExport';
import './TaxCenter.css';

export default function TaxCenterManager({states = []}) {
  const {fetch: authFetch, API_URL} = useAuth();
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState('');
  const [exportMessage, setExportMessage] = useState('');
  const [filters, setFilters] = useState({from: '', to: '', state: '',
    page: 1, pageSize: 50, sort: 'date', dir: 'desc'});
  const [selectedPeriod, setSelectedPeriod] = useState('all');
  const [draft, setDraft] = useState({from: '', to: '', state: ''});
  const {meta, rows, loading, error, reload} = useSellerTax(filters);
  const change = values => setFilters(current => ({...current, ...values}));
  async function exportCsv() {
    if (exporting) return;
    setExporting(true);
    setExportError('');
    setExportMessage('');
    try {
      await downloadTaxCsv(authFetch, API_URL, filters);
      setExportMessage('CSV download started.');
    } catch (failure) {
      setExportError(failure.message || 'Unable to export tax report. Please try again.');
    } finally {
      setExporting(false);
    }
  }
  function period(value) {
    setSelectedPeriod(value);
    if (value === 'custom') return;
    const year = new Date().getUTCFullYear();
    const dates = value === 'all' ? {from: '', to: ''} : value === 'last-year' ?
      {from: `${year - 1}-01-01`, to: `${year - 1}-12-31`} :
      {from: `${year}-01-01`, to: new Date().toISOString().slice(0, 10)};
    setDraft(current => ({...current, ...dates}));
  }
  return <div className="tax-center">
    <div className="tax-center__heading"><h2>Tax report</h2>
      <button type="button" className="tax-center__reload" aria-label="Refresh tax report"
        disabled={loading} onClick={reload}><i className="fa-solid fa-rotate-right"
          aria-hidden="true" /></button>
    </div>
    <p className="tax-center__muted">Planning estimates for US taxes, based on paid orders.
      State rates exclude local taxes. Federal estimates use a flat 27.3% rate.
      This report does not file or remit taxes. Consult a tax professional.</p>
    <form className="tax-center__filters" onSubmit={event => {
      event.preventDefault(); change({...draft, page: 1});
    }}>
      <label>Period<select aria-label="Period" value={selectedPeriod} onChange={event =>
        period(event.target.value)}>
        <option value="all">All time</option><option value="this-year">This year to date</option>
        <option value="last-year">Last year</option><option value="custom">Custom dates</option>
      </select></label>
      <label>From (UTC)<input type="date" value={draft.from} max={draft.to || undefined}
        onChange={event => { setSelectedPeriod('custom');
          setDraft(current => ({...current, from: event.target.value})); }} /></label>
      <label>To (UTC)<input type="date" value={draft.to} min={draft.from || undefined}
        onChange={event => { setSelectedPeriod('custom');
          setDraft(current => ({...current, to: event.target.value})); }} /></label>
      <label>Buyer state<select value={draft.state} onChange={event =>
        setDraft(current => ({...current, state: event.target.value}))}>
        <option value="">All states</option>{states.map(state =>
          <option key={state.code} value={state.code}>{state.name} ({state.code})</option>)}
      </select></label>
      <button type="submit" className="button small">Apply filters</button>
    </form>
    {loading && <p role="status">Loading tax report…</p>}
    {error && <div className="warning" role="alert"><p>{error}</p>
      <button type="button" className="button small secondary" onClick={reload}>Try again</button></div>}
    {!loading && !error && meta && <>
      <div className="tax-center__heading">
        <p>Reporting period: {meta.period.from || 'All time'} to {meta.period.to}</p>
        <button type="button" className="button small secondary" disabled={exporting}
          onClick={exportCsv}>{exporting ? 'Exporting…' : 'Export CSV'}</button>
      </div>
      {exportError && <p className="warning" role="alert">{exportError}</p>}
      {exportMessage && <p role="status">{exportMessage}</p>}
      <TaxSummaryCards meta={meta} />
      <TaxJurisdictionTable rows={meta.byJurisdiction} />
      <TaxOrderTable rows={rows} pagination={meta.pagination} filters={filters} onChange={change} />
    </>}
  </div>;
}
